import React, { FormEvent, MouseEvent, useState, useEffect } from 'react';
import { IStackTokens, ITextStyles, Stack, Text } from '@fluentui/react';
import { Checkbox } from '../components/common/checkbox';
import { InternalLinkSection } from '../components/internalLinkSection';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useFormValidationDialog } from '../helpers/hooks';
import { useAppDispatch, useAppSelector } from '../store';
import { ProgressDialog, WarningDialog } from '../components/dialogs';
import { Logger } from '../helpers';
import { appActions } from '../store/actions/app/actions';
import {
    getIntialEmailShareValues,
    ShareByEmailData,
} from '../form-loaders/shareByEmail';
import {
    shareByEmailScehma,
    shareWithCirclesSchema,
} from '../schemas/shareByEmail';
// eslint-disable-next-line max-len
import { ControlledTextField } from '../components/form/controls/controlledTextField';
import { SharePermissions } from 'nextcloud-api';
import { selectTranslate } from '../store/selectors/ui';
import { WizardFooter } from '../components/wizard/footer';
import {
    emailShareDndFilesThunk,
    shareDndFilesWithCircleThunk,
    shareServerFileByEmailThunk,
} from '../store/actions/app/thunks';
import { selectDestinationFolderPath } from '../store/selectors/app';
import {
    addUploadedSize,
    setProgressSize,
} from '../store/actions/app/actionCreators';
import { reduceFileSize } from '../helpers/file';
import { selectTeamsPathUploadFiles } from '../store/selectors/auth';
import { selectTeamsContext } from '../store/selectors/conversation';
import { Circle } from '../services/microsoft/msGraphApi';
import { selectCircles } from '../store/selectors/nextcloud';
import { shareSelectedFileWithCircleThunk } from '../store/actions/nextcloud/thunks';
import { DestinationFolder } from '../components/wizard/steps/dndFiles';
import { CircleSelector } from '../components/common/CircleSelector';
import {
    getIntialCircleShareValues,
    ShareWithCircleData,
} from '../form-loaders/shareWithCircle';

const containerTokens: IStackTokens = { childrenGap: 20 };

const pageDescriptionTextStyles: ITextStyles = {
    root: { lineHeight: 20, color: '#98908C' },
};

const emailContainerTokens: IStackTokens = { childrenGap: 8 };

export enum ShareTypePageVariant {
    Regular = 'regular',
    Dnd = 'dnd',
}

export type OrganizationSharePageProps = {
    variant: ShareTypePageVariant;
};

export const OrganizationSharePage = ({
    variant,
}: OrganizationSharePageProps): JSX.Element => {
    const dispatch = useAppDispatch();
    const translate = useAppSelector(selectTranslate);
    const destinationFolderPath = useAppSelector(selectDestinationFolderPath);
    const defaultUploadFolder = useAppSelector(selectTeamsPathUploadFiles);
    const teamsContext = useAppSelector(selectTeamsContext);
    const circles = useAppSelector(selectCircles);

    const [selectedCircle, setSelectedCircle] = useState<Circle[]>([]);
    const isChannelOrTeam = Boolean(
        teamsContext?.channel?.id || teamsContext?.team?.groupId,
    );
    const getInitialFormValues = () => {
        if (isChannelOrTeam) {
            return getIntialCircleShareValues();
        }
        return getIntialEmailShareValues();
    };

    const initialFormValues = getInitialFormValues();
    const preselectedCircle = (initialFormValues as ShareWithCircleData)
        .preselectedCircle;

    useEffect(() => {
        if (preselectedCircle) {
            setSelectedCircle([preselectedCircle]);
        }
    }, [preselectedCircle]);

    const {
        watch,
        register,
        setValue,
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<ShareByEmailData | ShareWithCircleData>({
        defaultValues: initialFormValues,
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        resolver: zodResolver(
            isChannelOrTeam ? shareWithCirclesSchema : shareByEmailScehma,
        ),
        criteriaMode: 'firstError',
    });

    const { errorToDisplay, showValidationDialog, setShowValidationDialog } =
        useFormValidationDialog(errors);

    const [progressDialogHidden, setProgressDialogHidden] = useState(true);

    const onUpload = async (data: ShareByEmailData, e: FormEvent) => {
        e.preventDefault();
        Logger.info('onUpload called');

        const { files, email, permissions } = data;

        if (variant === ShareTypePageVariant.Regular) {
            if (isChannelOrTeam) {
                dispatch(
                    shareSelectedFileWithCircleThunk(
                        files[0],
                        selectedCircle[0],
                    ),
                );
                return;
            }
            if (email) {
                dispatch(
                    shareServerFileByEmailThunk(email, files[0], permissions),
                );
                return;
            }

            dispatch(
                shareSelectedFileWithCircleThunk(files[0], selectedCircle[0]),
            );
            return;
        }

        const uploadFolder: DestinationFolder = destinationFolderPath
            ? destinationFolderPath
            : defaultUploadFolder;

        const totalSize = files.reduce(reduceFileSize, 0);

        setProgressDialogHidden(false);
        dispatch(setProgressSize(totalSize));

        if (variant === ShareTypePageVariant.Dnd) {
            if (email) {
                await dispatch(
                    emailShareDndFilesThunk({
                        email,
                        files,
                        folder: uploadFolder,
                        permissions,
                        onUploadProgress: (progress) =>
                            dispatch(addUploadedSize(progress)),
                    }),
                );
                return;
            }
            if (isChannelOrTeam) {
                await dispatch(
                    shareDndFilesWithCircleThunk({
                        files,
                        folder: uploadFolder,
                        selectedCircle: selectedCircle[0],
                        onUploadProgress: (progress) =>
                            dispatch(addUploadedSize(progress)),
                    }),
                );
                return;
            }
        }
    };

    const hideValidationDialog = () => setShowValidationDialog(false);

    const permissionsGranted = watch('permissions') === SharePermissions.Update;
    const isDropdownDisabled = watch('isDropdownDisabled');

    const onPermissionChange = (e: FormEvent<HTMLInputElement>): void => {
        const permissions = e.currentTarget.checked
            ? SharePermissions.Update
            : SharePermissions.Read;

        setValue('permissions', permissions);
    };

    const onInternalLinkClick = (e: MouseEvent<HTMLButtonElement>): void => {
        e.preventDefault();
        dispatch(appActions.shareLink(watch('files')));
    };

    return (
        <form onSubmit={handleSubmit(onUpload)}>
            <Stack tokens={containerTokens}>
                <Text variant="medium" styles={pageDescriptionTextStyles}>
                    {translate('organization_share_description')}
                </Text>
                <Stack tokens={emailContainerTokens}>
                    {isChannelOrTeam ? (
                        <Stack tokens={{ childrenGap: 8 }}>
                            <CircleSelector
                                circles={circles}
                                selectedCircleIds={selectedCircle.map(
                                    (circle) => circle.id,
                                )}
                                onSelectionChange={setSelectedCircle}
                                searchPlaceholder={translate('search')}
                                dropdownPlaceholder={
                                    preselectedCircle?.name ||
                                    translate('no_team_selected')
                                }
                                isMultiSelect={false}
                                isDropdownDisabled={isDropdownDisabled}
                            />
                        </Stack>
                    ) : (
                        <ControlledTextField
                            label={translate('feedback_contactemail')}
                            control={control}
                            {...register('email')}
                        />
                    )}
                    <Checkbox
                        color="#F36928"
                        checked={isChannelOrTeam || permissionsGranted}
                        disabled={isChannelOrTeam}
                        label={translate('grant_update_permissions')}
                        onChange={onPermissionChange}
                    />
                </Stack>
                {variant === ShareTypePageVariant.Regular && (
                    <InternalLinkSection onClick={onInternalLinkClick} />
                )}
            </Stack>
            <WizardFooter />
            <WarningDialog
                hidden={!showValidationDialog}
                subText={errorToDisplay?.message}
                icon="ErrorBadge"
                color="red"
                onDismiss={hideValidationDialog}
            />
            {variant === ShareTypePageVariant.Dnd && (
                <ProgressDialog
                    hidden={progressDialogHidden}
                    onDismiss={setProgressDialogHidden}
                    title={translate('progress_header')}
                    subText={translate('progress_count')}
                />
            )}
        </form>
    );
};
